import { Button } from '@bart.sk-eshop/eshop-defaults/lib/components/Golfcentrum/General/Button';
import { rem } from 'polished';
import * as React from 'react';
import { __ } from '@bart.sk-eshop/react-i18n';
import styled, { css } from 'styled-components';

interface Props {
  visible: boolean;
  image: string;
  name: string;
  price: number | string;
  oldPrice?: string;
  currency: string;
  onAddToCart: () => void;
  disabled?: boolean;
  cartIsFetching?: boolean;
  errorMessage?: string;
}

// Compact floating card shown once the main buy box has scrolled out of
// view, so the name/price/add-to-cart action stays reachable without
// scrolling back up. Anchored bottom-left (rather than a full-width bar) so
// it never overlaps the chat widget bubble, which sits bottom-right.
function StickyAddToCartBar({
  visible,
  image,
  name,
  price,
  oldPrice,
  currency,
  onAddToCart,
  disabled,
  cartIsFetching,
  errorMessage,
}: Props) {
  return (
    <Wrapper visible={visible} role="complementary" aria-hidden={!visible}>
      <Inner>
        {image && <Image src={image} alt={name} />}
        <Name>{name}</Name>
        <PriceWrapper>
          <Price>{`${price} ${currency}`}</Price>
          {oldPrice && +oldPrice > +price ? (
            <OldPrice>{`${oldPrice} ${currency}`}</OldPrice>
          ) : null}
        </PriceWrapper>
        <ButtonWrapper>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <CartButton disabled={disabled} type="button" onClick={onAddToCart}>
            {cartIsFetching ? __('Pridávam do košíka') : __('Do košíka')}
          </CartButton>
        </ButtonWrapper>
      </Inner>
    </Wrapper>
  );
}

const Wrapper = styled.div<{ visible: boolean }>`
  position: fixed;
  left: ${rem(24)};
  bottom: ${rem(24)};
  z-index: 90;
  max-width: ${rem(560)};
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(8)};
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: translateY(${({ visible }) => (visible ? '0' : rem(16))});
  transition: opacity 0.25s ease, transform 0.25s ease;
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};

  ${({ theme }) => theme.mediab.l925`
    left: ${rem(12)};
    right: ${rem(92)};
    bottom: ${rem(12)};
    max-width: none;
  `}
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding: ${rem(12)} ${rem(16)};

  ${({ theme }) => theme.mediab.l925`
    padding: ${rem(8)} ${rem(12)};
    gap: ${rem(8)};
  `}
`;

const imageSize = css`
  width: ${rem(48)};
  height: ${rem(48)};

  ${({ theme }) => theme.mediab.s450`
    display: none;
  `}
`;

const Image = styled.img`
  ${imageSize}
  object-fit: contain;
  flex: 0 0 auto;
`;

const Name = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.color.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ theme }) => theme.mediab.l925`
    font-size: ${rem(12)};
  `}
`;

const PriceWrapper = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: baseline;
  gap: ${rem(8)};
  white-space: nowrap;
`;

const Price = styled.span`
  font-family: ${({ theme }) => theme.font.secondary};
  font-size: ${rem(18)};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const OldPrice = styled.span`
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.color.gray48};
  text-decoration: line-through;
`;

const ButtonWrapper = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

const ErrorMessage = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: ${rem(6)};
  padding: ${rem(4)} ${rem(10)};
  background: ${({ theme }) => theme.color.red || '#d0021b'};
  color: ${({ theme }) => theme.color.white};
  font-size: ${rem(12)};
  border-radius: ${rem(2)};
  white-space: nowrap;
`;

const CartButton = styled(Button)`
  height: ${rem(40)};
  padding: ${rem(8)} ${rem(20)};

  ${({ theme }) => theme.mediab.l925`
    height: ${rem(36)};
    padding: ${rem(6)} ${rem(14)};
    font-size: ${rem(12)};
  `}
`;

export { StickyAddToCartBar };
